import React from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { DiscountTag, HotelBed } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { MapPinWithLabel } from '@shared/components/JoyInteractableMap/MapPin';
import type { AccommodationListingDTO } from '@shared/types/Accommodations.types';
import type { MapPinColor } from '@shared/components/JoyInteractableMap/MapPin.styles';
import { useAccommodationMapListSyncData } from '../AccommodationMapListSync/AccommodationMapListSync';

type MapMarkerProps = Readonly<{
  hotel: AccommodationListingDTO;
  isHighlighted: boolean;
  activeMarker?: string;
  onClickMarker: (hotel: AccommodationListingDTO) => void;
  isHoveringAddedHotel?: boolean;
  /** Optional raw color override for the marker pin (e.g. the cash-fund gold). Label turns white. */
  markerColor?: MapPinColor;
  /** Optional label override (e.g. the cash-fund "+$X"). Falls back to the nightly price. */
  priceLabelOverride?: string;
}>;

const getLabelColor = (isPickedByCouple: boolean | undefined, isSelected: boolean) => {
  if (isPickedByCouple || isSelected) return 'white';
  return undefined;
};

export const MapMarker = React.memo<MapMarkerProps>(props => {
  const { hotel, isHighlighted, activeMarker, onClickMarker, isHoveringAddedHotel, markerColor, priceLabelOverride } = props;
  const { hoveredHotelId } = useAccommodationMapListSyncData();
  const { tq } = useTranslation('accommodationsConsole');

  const isCardVisible = activeMarker === hotel.joyPlaceId;
  const price = priceLabelOverride ?? hotel.pricingInfo.values?.nightlyBaseRateString;
  // No discount tag next to the cash-fund "+$X" label — the override reframes price as out-of-pocket, not a discount.
  const hasDiscount = !priceLabelOverride && !!(hotel.pricingInfo.values?.discountPercentage && hotel.pricingInfo.values?.strikeoutNightlyBaseRateString);
  const isHovered = hotel.joyPlaceId === hoveredHotelId;
  const shouldShowOnTop = isHighlighted || isHovered || hotel.isPickedByCouple;
  const isSelected = isHighlighted || isCardVisible;

  const labelColor = markerColor ? 'white' : getLabelColor(hotel.isPickedByCouple, isSelected);
  const pinColor = markerColor ?? (hotel.isPickedByCouple ? 'newUIBrandPrimary' : undefined);

  const handleMapPin = useEventCallback(e => {
    e.stopPropagation();
    onClickMarker(hotel);
  });

  return (
    <MapPinWithLabel
      key={hotel.joyPlaceId}
      position={hotel.location}
      isActive={isHighlighted}
      showOnTop={shouldShowOnTop}
      isHoverBounce={isHovered}
      {...(isHoveringAddedHotel && { transform: 'scale(1.2)' })}
      showImageOnHover={!hotel.isPickedByCouple}
      isCardVisible={isCardVisible}
      onClick={handleMapPin}
      mapPinColor={pinColor}
      hoverColor={pinColor}
      mapPinHighlightColor={markerColor}
      onDoubleClick={e => {
        e.stopPropagation();
      }}
      image={hotel.mediaAssets?.photos?.[0]?.url ? `url(${hotel.mediaAssets?.photos?.[0]?.url})` : ''}
      label={
        <Flex
          justifyContent="center"
          alignItems="center"
          columnGap={2}
          color={labelColor}
        >
          {price ? <TextV2 typographyVariant="label3">{price}</TextV2> : <HotelBed size="sm" />}
          {hasDiscount && (
            <DiscountTag
              size={16}
              title={tq`Exclusive Offer`}
            />
          )}
        </Flex>
      }
    />
  );
});
